import { useEffect, useState } from 'react';
import Dialogo from '@/components/Dialogo';
import { criarCanal, mensagemDoErro, papeisDisponiveis, sugerirInstancia } from '@/services/canaisService';
import { normalizarTelefone } from '@/lib/telefone';

/**
 * Cadastro de um canal que conecta por QR Code.
 *
 * Cadastrar não conecta: a instância nasce inerte e o canal entra na lista como
 * "Desconectado". O QR só nasce depois, no botão Conectar, com o gestor de
 * celular na mão — o código expira em segundos, e emiti-lo aqui começaria um
 * relógio que ninguém está olhando (docs/plano-tela-canais.md).
 */
export default function DialogoNovoCanal({ aberto, aoFechar, clinicaId, numeros }) {
  const disponiveis = papeisDisponiveis(numeros);
  const [papel, setPapel] = useState('');
  const [telefone, setTelefone] = useState('');
  const [instancia, setInstancia] = useState('');
  const [erro, setErro] = useState(null);
  const [salvando, setSalvando] = useState(false);

  // Ao abrir, escolhe o primeiro papel livre e sugere o nome da instância a
  // partir do que a clínica já usa (<prefixo>-recepcao → <prefixo>-resgate).
  useEffect(() => {
    if (!aberto) return;
    const inicial = disponiveis[0]?.id ?? '';
    setPapel(inicial);
    setInstancia(sugerirInstancia(numeros, inicial));
    setTelefone('');
    setErro(null);
    // `disponiveis`/`numeros` mudam de identidade a cada render do pai; entrar
    // nas dependências reabriria o formulário em branco enquanto o gestor digita.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto]);

  function trocarPapel(e) {
    const novo = e.target.value;
    setPapel(novo);
    setInstancia(sugerirInstancia(numeros, novo));
  }

  function fechar() {
    setErro(null);
    aoFechar();
  }

  async function salvar(evento) {
    evento.preventDefault();
    setErro(null);
    setSalvando(true);
    try {
      await criarCanal({
        clinicaId,
        papel,
        // Normaliza antes de enviar para o gestor poder digitar "(62) 90000-0001".
        // O servidor valida E.164 de novo — ele é a autoridade.
        telefone: normalizarTelefone(telefone),
        instancia: instancia.trim().toLowerCase(),
      });
      // A lista se atualiza sozinha: o ouvirNumeros é um listener do Firestore.
      fechar();
    } catch (e) {
      console.error('[OdontoSDR] Falha ao criar canal:', e);
      setErro(mensagemDoErro(e, 'Não foi possível cadastrar o canal.'));
    } finally {
      setSalvando(false);
    }
  }

  const semVagas = disponiveis.length === 0;

  return (
    <Dialogo titulo="Adicionar canal" aberto={aberto} aoFechar={fechar} largura={480}>
      {semVagas ? (
        <div className="formulario">
          <p className="vazio">
            A clínica já tem um canal para cada papel que conecta por QR Code. Para trocar um
            número, reconecte o canal existente.
          </p>
          <footer className="dialogo-acoes">
            <button type="button" className="btn-secundario" onClick={fechar}>
              Fechar
            </button>
          </footer>
        </div>
      ) : (
        <form className="formulario" onSubmit={salvar}>
          <div className="campo">
            <label htmlFor="nc-papel">Papel do número</label>
            <select id="nc-papel" required value={papel} onChange={trocarPapel}>
              {disponiveis.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
            {papel && <p className="campo-ajuda">{disponiveis.find((p) => p.id === papel)?.descricao}</p>}
          </div>

          <div className="campo">
            <label htmlFor="nc-telefone">Telefone do número</label>
            <input
              id="nc-telefone"
              required
              placeholder="+55 62 90000-0001"
              value={telefone}
              onChange={(e) => setTelefone(e.target.value)}
            />
            <p className="campo-ajuda">
              É o número do chip que vai ler o QR. Guardado no formato internacional.
            </p>
          </div>

          <div className="campo">
            <label htmlFor="nc-instancia">Nome da instância</label>
            <input
              id="nc-instancia"
              required
              pattern="[a-z0-9][a-z0-9\-]{2,49}"
              placeholder="clinica-exemplo-vendas"
              value={instancia}
              onChange={(e) => setInstancia(e.target.value)}
            />
            <p className="campo-ajuda">
              Identificador técnico do canal. Minúsculas, números e hífen. Precisa ser único
              entre todas as clínicas.
            </p>
          </div>

          {erro && <p className="erro">{erro}</p>}

          <footer className="dialogo-acoes">
            <button type="button" className="btn-secundario" onClick={fechar}>
              Cancelar
            </button>
            <button type="submit" className="btn-primario" disabled={salvando}>
              {salvando ? 'Cadastrando…' : 'Cadastrar canal'}
            </button>
          </footer>
        </form>
      )}
    </Dialogo>
  );
}
